import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../../utils/services/api.service";
import { ENV } from "../../constants";
import { useAuth } from "../auth";
import { useLocation } from "react-router-dom";

interface InvoiceStatusCounts {
  waiting_for_approval: number;
  scheduled: number;
}

interface InvoiceCountContextType {
  statusCounts: InvoiceStatusCounts;
  loading: boolean;
  refreshCounts: () => Promise<void>;
}

const defaultContext: InvoiceCountContextType = {
  statusCounts: {
    waiting_for_approval: 0,
    scheduled: 0
  },
  loading: false,
  refreshCounts: async () => {}
};

const InvoiceCountContext = createContext<InvoiceCountContextType>(defaultContext);

export const useInvoiceCount = () => useContext(InvoiceCountContext);

export const InvoiceCountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  const location = useLocation();
  const [statusCounts, setStatusCounts] = useState<InvoiceStatusCounts>({
    waiting_for_approval: 0,
    scheduled: 0
  });
  const [loading, setLoading] = useState<boolean>(false);

  const fetchInvoiceStatusCounts = useCallback(async () => {
    if (!state.user || state.phase !== "auth") {
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`${ENV.getApiEndPoint()}/api/invoice/count`);
      if (response.data && response.data.success) {
        setStatusCounts(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch invoice counts", error);
    } finally {
      setLoading(false);
    }
  }, [state.user, state.phase]);

  useEffect(() => {
    if (state.user && state.phase === "auth") {
      fetchInvoiceStatusCounts();
    }
  }, [fetchInvoiceStatusCounts, state.user, state.phase, location.pathname]);

  return (
    <InvoiceCountContext.Provider
      value={{
        statusCounts,
        loading,
        refreshCounts: fetchInvoiceStatusCounts
      }}
    >
      {children}
    </InvoiceCountContext.Provider>
  );
};